import type { DocumentTagsDialogContentFragment } from "../client/generated";
import { Trans, useTranslation } from "react-i18next";
import { AccessButton } from "@acdc2/ui/components/access-button";
import { DocumentTagsInput } from "./DocumentTagsInput";
import { DocumentTagsSuggestions } from "./DocumentTagsSuggestions";
import { assertTypeName } from "../client/guards";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogLoading,
  DialogTitle,
} from "@acdc2/ui/components/dialog";
import {
  useDocumentTagsDialogContentQuery,
  useTagDocumentMutation,
  useUntagDocumentMutation,
} from "../client/generated";

type Props = {
  fragment: DocumentTagsDialogContentFragment;
  isOpen: boolean;
};

export function DocumentTagsDialogContent({ fragment, isOpen }: Props) {
  const { t } = useTranslation();
  const [tagDocument] = useTagDocumentMutation();
  const [untagDocument] = useUntagDocumentMutation();

  const { data } = useDocumentTagsDialogContentQuery({
    variables: { documentId: fragment.id },
    skip: !isOpen,
  });

  if (!data) return <DialogLoading />;
  const { document } = data;
  assertTypeName(document?.__typename === "Document");

  const onTagAdd = async (tag: string) => {
    try {
      await tagDocument({
        variables: {
          documentId: document.id,
          tag,
        },
        optimisticResponse: {
          addDocumentTag: {
            userErrors: [],
            document: {
              id: document.id,
              tags: [...document.tags, tag].sort(),
              possibleTags: document.possibleTags.filter((t) => t !== tag),
            },
          },
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const onTagRemove = async (tag: string) => {
    try {
      await untagDocument({
        variables: {
          documentId: document.id,
          tag,
        },
        optimisticResponse: {
          removeDocumentTag: {
            userErrors: [],
            document: {
              id: document.id,
              tags: document.tags.filter((t) => t !== tag),
              possibleTags: [...document.possibleTags, tag].sort(),
            },
          },
        },
      });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <DialogContent showCloseButton={false}>
      <DialogHeader>
        <DialogTitle>{t("DocumentTagsDialogContent.title")}</DialogTitle>
        <DialogDescription>
          <Trans
            i18nKey="DocumentTagsDialogContent.description"
            components={{ "word-break": <span className="break-all" /> }}
            values={{ name: document.name }}
          />
        </DialogDescription>
      </DialogHeader>

      <DocumentTagsInput
        fragment={document}
        onTagAdd={onTagAdd}
        onTagRemove={onTagRemove}
      />

      <div className="flex flex-col gap-3">
        <DialogTitle asChild>
          <h3>{t("DocumentTagsDialogContent.suggestions")}</h3>
        </DialogTitle>
        <DocumentTagsSuggestions fragment={document} onTagAdd={onTagAdd} />
      </div>

      <DialogFooter>
        <DialogClose asChild>
          <AccessButton>{t("DocumentTagsDialogContent.done")}</AccessButton>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  );
}
